/**
 * Manual Control for Cashflow & Age Pension — move your own money, and watch the three figures.
 *
 * WHY IT IS ITS OWN ALLOCATOR rather than the shared ProceedsDeploymentFields. Every other module's
 * Manual Control deploys PROCEEDS: a lump sum a sale has just created, sitting outside the balance
 * sheet waiting to be placed. There is no sale here. The client is REARRANGING what they already
 * own, so the pool is their existing liquid assets and every dollar allocated has to leave something
 * they currently hold. Reusing an allocator built around "here is $400,000, where does it go?" would
 * have modelled money appearing from nowhere.
 *
 * IT SHARES THE ENGINE WITH THE SUGGESTION ENGINE, deliberately. A manual allocation is turned into
 * exactly the same candidate shape the engine ranks, and evaluated by the same call. Two paths to
 * one answer is how a module comes to show a client two different numbers for the same plan.
 */
import React, { useMemo } from 'react';
import { PiggyBank, Gift, ShieldCheck, CreditCard, AlertTriangle } from 'lucide-react';
import { CurrencyInput, InsightCard, IconTile } from '../../components/ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { AnnuityTerms } from '../../components/strategy/AnnuityTerms.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { FUNERAL_BOND_LIMIT, GIFTING_LIMIT_ANNUAL } from '../../constants/index.ts';
import {
    type ManualAllocation,
    type ManualAnnuityTerms,
    type LiquidSource,
    allocationTotal,
} from './cashflowPensionScenario.ts';
import SupportAtHomeAnnuityConfigurator, {
    type AnnuityDetails,
    type AnnuityFundingSource,
} from '../supportAtHome/SupportAtHomeAnnuityConfigurator.tsx';
import { buildAnnuityImpact, summariseAnnuityImpact } from '../../../../shared/src/calculation/annuityStrategies.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import { calculateAge } from '../../utils/index.ts';

/** A reserve the screen warns about spending, rather than silently allowing it to vanish. */
export const SUGGESTED_RESERVE = 50_000;

/**
 * The smallest lifetime annuity the engine will model, mirroring MIN_ANNUITY_PURCHASE in
 * server/.../meansTestStrategies.ts. Below it the lever correctly does nothing, and the client is
 * told why rather than left looking at a figure that refuses to move.
 */
export const MIN_ANNUITY_PURCHASE = 50_000;

interface Props {
    factFind: DraftFactFind;
    sources: LiquidSource[];
    allocation: ManualAllocation;
    onChange: (next: ManualAllocation) => void;
    /** Total debt available to repay; 0 hides that row. */
    totalDebt: number;
    /** How the annuity is structured — owner, residual, reversionary, indexation, quoted income. */
    annuityTerms: ManualAnnuityTerms;
    onAnnuityTermsChange: (next: ManualAnnuityTerms) => void;
}

const LEVERS: Array<{
    key: keyof ManualAllocation;
    icon: typeof PiggyBank;
    label: string;
    help: string;
}> = [
    {
        key: 'funeralBond',
        icon: ShieldCheck,
        label: 'Into a funeral bond',
        help: 'Exempt from the assets test up to the published limit per person, and it pays for something the family would fund anyway. Above the limit it is counted like any other asset.',
    },
    {
        key: 'gift',
        icon: Gift,
        label: 'Given away',
        help: 'Inside the yearly free area it leaves the assets test straight away. Above it, the excess is still counted — and still deemed to earn income — for five years, even though the money has gone.',
    },
    {
        key: 'annuity',
        icon: PiggyBank,
        label: 'Into a lifetime annuity',
        help: 'Only 60% of the purchase price is assessed as an asset, so it moves more than anything else here. How much of the capital stays available depends on the shape you pick below.',
    },
    {
        key: 'debtRepayment',
        icon: CreditCard,
        label: 'Off your debt',
        help: 'Repaying debt does not change your net worth on its own, but it frees the repayments — which is cashflow you keep every year.',
    },
];

export function CashflowManualControl({
    factFind,
    sources,
    allocation,
    onChange,
    totalDebt,
    annuityTerms,
    onAnnuityTermsChange,
}: Props) {
    const available = sources.reduce((sum, s) => sum + s.value, 0);

    const clientAge = calculateAge(factFind?.client?.dob);
    const partnerAge = factFind?.adviceFor === 'couple' ? calculateAge(factFind?.partner?.dob) : null;

    /*
     * The configurator speaks `AnnuityDetails`; this module stores the four terms plus the quote.
     * Adapted in both directions here rather than widening the module's own state to the
     * configurator's shape — the amount belongs to the slider, and only these five fields are the
     * client's to set.
     */
    const annuityFundingSources: AnnuityFundingSource[] = useMemo(
        () => sources.map((source) => ({
            id: `${source.type}-${source.id}`,
            type: source.type,
            assetId: source.id,
            label: `${source.description} — ${formatCurrency(source.value)} available`,
            available: source.value,
        })),
        [sources],
    );

    const annuityDetails: AnnuityDetails = useMemo(() => ({
        annuityType: 'lifetime',
        purchaseAmount: allocation.annuity,
        residualCapital: annuityTerms.residualCapital,
        termYears: null,
        indexation: annuityTerms.indexation,
        reversionary: annuityTerms.reversionary,
        owner: annuityTerms.owner,
        incomeOverride: annuityTerms.incomeOverride,
        fundingSource: annuityFundingSources[0] || null,
    }), [allocation.annuity, annuityTerms, annuityFundingSources]);

    const applyAnnuityDetails = (updater: AnnuityDetails | ((prev: AnnuityDetails) => AnnuityDetails)) => {
        const next = typeof updater === 'function' ? updater(annuityDetails) : updater;
        onAnnuityTermsChange({
            residualCapital: Math.max(0, Math.min(1, Number(next.residualCapital) || 0)),
            owner: (next.owner as ManualAnnuityTerms['owner']) || 'client',
            reversionary: !!next.reversionary,
            indexation: !!next.indexation,
            incomeOverride: next.incomeOverride != null && Number.isFinite(Number(next.incomeOverride))
                ? Number(next.incomeOverride)
                : null,
        });
    };

    /*
     * The live outcome, built the SAME way the engine builds it, so the income shown beside the
     * controls is the income the projection runs on.
     */
    const annuitySummary = useMemo(() => {
        if (!(allocation.annuity >= MIN_ANNUITY_PURCHASE)) return null;
        try {
            const record = buildAnnuityImpact(factFind as unknown as FactFind, {
                annuityType: 'lifetime',
                purchaseAmount: allocation.annuity,
                age: clientAge ?? 70,
                partnerAge: annuityTerms.owner === 'joint' || annuityTerms.reversionary ? partnerAge : null,
                indexation: annuityTerms.indexation,
                residualCapital: annuityTerms.residualCapital,
                owner: annuityTerms.owner,
                reversionary: annuityTerms.reversionary,
                incomeOverride: annuityTerms.incomeOverride,
            });
            return summariseAnnuityImpact(record);
        } catch {
            return null;
        }
    }, [factFind, allocation.annuity, annuityTerms, clientAge, partnerAge]);
    const allocated = allocationTotal(allocation);
    const remaining = available - allocated;
    const people = factFind?.adviceFor === 'couple' ? 2 : 1;
    const bondLimit = FUNERAL_BOND_LIMIT * people;

    const set = (key: keyof ManualAllocation, value: number | undefined) =>
        onChange({ ...allocation, [key]: Math.max(0, value ?? 0) });

    return (
        <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-card">
                <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
                    <div>
                        <h3 className="text-base font-bold text-gray-800">Move your money</h3>
                        <p className="mt-0.5 text-sm text-slate-600">
                            You have <strong>{formatCurrency(available)}</strong> you could move — cash, term deposits
                            and shares. Your home and super pension aren&apos;t included.
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Still unallocated</p>
                        <p className={`text-xl font-bold tabular-nums ${remaining < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                            {formatCurrency(remaining)}
                        </p>
                    </div>
                </div>

                <div className="space-y-3">
                    {LEVERS.filter((lever) => lever.key !== 'debtRepayment' || totalDebt > 0).map((lever) => (
                        <div key={lever.key} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3.5">
                            <div className="flex flex-wrap items-start gap-3">
                                <IconTile icon={lever.icon} tone="brand" variant="soft" size="sm" />
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-bold text-gray-800">{lever.label}</p>
                                    <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{lever.help}</p>
                                </div>
                                <div className="w-44 shrink-0">
                                    <CurrencyInput
                                        label=""
                                        value={allocation[lever.key] || ''}
                                        onChange={(v) => set(lever.key, v)}
                                        className=""
                                    />
                                </div>
                            </div>

                            {/*
                              * The consequence at the point of the decision — the pattern this app
                              * already uses for CGT under the asset being sold. A limit that is only
                              * mentioned in a tooltip is a limit the client will exceed.
                              */}
                            {lever.key === 'funeralBond' && allocation.funeralBond > bondLimit && (
                                <p className="mt-2 rounded-lg bg-white border border-slate-200 border-l-4 border-l-amber-400 px-3 py-2 text-xs text-slate-700">
                                    Only <strong>{formatCurrency(bondLimit)}</strong> is exempt
                                    {people > 1 ? ' between you' : ''}. The{' '}
                                    <strong>{formatCurrency(allocation.funeralBond - bondLimit)}</strong> above that is
                                    counted like any other asset — you would be locking money away for no means-test gain.
                                </p>
                            )}
                            {lever.key === 'gift' && allocation.gift > GIFTING_LIMIT_ANNUAL && (
                                <p className="mt-2 rounded-lg bg-white border border-slate-200 border-l-4 border-l-amber-400 px-3 py-2 text-xs text-slate-700">
                                    Above the <strong>{formatCurrency(GIFTING_LIMIT_ANNUAL)}</strong> yearly free area,
                                    the extra <strong>{formatCurrency(allocation.gift - GIFTING_LIMIT_ANNUAL)}</strong> is
                                    still counted as yours — and still deemed to earn income — for <strong>five years</strong>.
                                    The money is gone; the means test has not noticed.
                                </p>
                            )}
                            {/*
                              * A minimum the client cannot see is a minimum they will fall foul of.
                              * Below it there is no annuity to buy, so the engine correctly does
                              * nothing — and without this line the screen would just show no change
                              * and leave them wondering whether the tool was broken.
                              */}
                            {lever.key === 'annuity' && allocation.annuity > 0 && allocation.annuity < MIN_ANNUITY_PURCHASE && (
                                <p className="mt-2 rounded-lg bg-white border border-slate-200 border-l-4 border-l-amber-400 px-3 py-2 text-xs text-slate-700">
                                    Providers don&apos;t generally write lifetime annuities below{' '}
                                    <strong>{formatCurrency(MIN_ANNUITY_PURCHASE)}</strong>, so we haven&apos;t modelled
                                    this one. Raise it, or put the money somewhere else on this page.
                                </p>
                            )}
                            {/*
                              * THE TERMS, STATED (owner, 2026-09-01). This module's annuity is built
                              * with owner 'client', nil residual, not reversionary and not indexed —
                              * chosen by the code, not the client. For a couple the non-reversionary
                              * default means the income stops on the owner's death and the survivor
                              * receives nothing from it, which is the most consequential thing on
                              * this row and was nowhere on screen.
                              */}
                            {lever.key === 'annuity' && allocation.annuity >= MIN_ANNUITY_PURCHASE && (
                                <div className="mt-2 space-y-2">
                                    {/*
                                      * THE SAME CONFIGURATOR EVERY OTHER MODULE USES (owner,
                                      * 2026-09-01). Aged Care, Support at Home, the Retirement
                                      * Village, the Downsizer and the Granny Flat all let a client
                                      * set the owner, the residual capital, whether it is
                                      * reversionary and whether it is indexed — and enter a real
                                      * provider quote. This module offered a slider and decided all
                                      * five itself, which is the one thing a client cannot see and
                                      * the terms card then reported faithfully about a product
                                      * nobody had chosen.
                                      *
                                      * The AMOUNT stays on the slider above: the configurator does
                                      * not own it, and two inputs for one number is how the two
                                      * disagree.
                                      */}
                                    <SupportAtHomeAnnuityConfigurator
                                        factFind={factFind}
                                        annuityDetails={annuityDetails}
                                        setAnnuityDetails={applyAnnuityDetails}
                                        annuitySummary={annuitySummary}
                                        annuityWarnings={[]}
                                        fundingSources={annuityFundingSources}
                                        formatCurrency={formatCurrency}
                                        clientAge={clientAge}
                                        partnerAge={partnerAge}
                                    />
                                    <AnnuityTerms
                                        purchaseAmount={allocation.annuity}
                                        residualPercent={Math.round(Math.max(0, Math.min(1, annuityTerms.residualCapital)) * 100)}
                                        ownerName={annuityTerms.owner === 'partner'
                                            ? (factFind?.partner?.firstName || 'your partner')
                                            : (factFind?.client?.firstName || 'you')}
                                        partnerName={factFind?.adviceFor === 'couple'
                                            ? (annuityTerms.owner === 'partner'
                                                ? (factFind?.client?.firstName || 'you')
                                                : (factFind?.partner?.firstName || 'your partner'))
                                            : null}
                                        /* A jointly owned annuity keeps paying the survivor by its ownership. */
                                        reversionary={annuityTerms.owner === 'joint' || annuityTerms.reversionary}
                                        indexed={annuityTerms.indexation}
                                        annuityType="lifetime"
                                        annualPayment={Number(annuitySummary?.annualPayment) || null}
                                    />
                                </div>
                            )}
                            {lever.key === 'debtRepayment' && allocation.debtRepayment > totalDebt && (
                                <p className="mt-2 rounded-lg bg-white border border-slate-200 border-l-4 border-l-amber-400 px-3 py-2 text-xs text-slate-700">
                                    You only owe <strong>{formatCurrency(totalDebt)}</strong>.
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {remaining < 0 && (
                <InsightCard tone="caution" heading="That is more than you have" icon={AlertTriangle}>
                    <p>
                        You have allocated <strong>{formatCurrency(allocated)}</strong> from{' '}
                        <strong>{formatCurrency(available)}</strong>. Reduce something before the figures below mean
                        anything.
                    </p>
                </InsightCard>
            )}

            {remaining >= 0 && remaining < SUGGESTED_RESERVE && allocated > 0 && (
                /*
                 * Not a block — an adviser may have good reason. But someone paying for help at home
                 * needs money they can reach this month, and every lever here locks capital away to
                 * some degree: a gift is gone, a bond is untouchable until death, an annuity is
                 * committed for life. Improving a means test while leaving nothing accessible is a
                 * bad outcome the numbers alone would report as a win.
                 */
                <InsightCard tone="caution" heading="Very little left to hand" icon={AlertTriangle}>
                    <p>
                        This leaves <strong>{formatCurrency(remaining)}</strong> you can actually reach. A gift is gone,
                        a funeral bond can&apos;t be touched, and an annuity is committed for life — so keeping something
                        accessible matters more than the last bit of pension.
                    </p>
                </InsightCard>
            )}
        </div>
    );
}

export default CashflowManualControl;
